import React, { PropsWithChildren, Children, useCallback } from 'react';
import classNames from 'classnames';
import { NativeProps } from '@/src/components/interfaces';
import { CButton, CButtonPattern, CButtonSize, CButtonShape } from '@/src/components/c-button/CButton';
import { CFlex, AlignItems, FlexDirection } from '@/src/components/c-flex/CFlex';
import { CIcons } from '@/src/components/c-icon/CIcon';

export enum CToggleGroupOrientation {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export interface CToggleGroupItemProps extends NativeProps {
  value: string;
  icon?: CIcons;
  text?: string;
  disabled?: boolean;
}

export function CToggleGroupItem(_props: PropsWithChildren<CToggleGroupItemProps>): React.JSX.Element {
  return null as unknown as React.JSX.Element;
}

export interface CToggleGroupProps extends NativeProps {
  value: string[];
  exclusive?: boolean;
  orientation?: CToggleGroupOrientation;
  size?: CButtonSize;
  onChange: (value: string[]) => void;
  ariaLabel?: string;
}

export function CToggleGroup(props: PropsWithChildren<CToggleGroupProps>): React.JSX.Element {
  const orientation = props.orientation ?? CToggleGroupOrientation.Horizontal;
  const size = props.size ?? CButtonSize.Md;
  const exclusive = props.exclusive ?? false;

  const items = Children.toArray(props.children).filter(
    (child): child is React.ReactElement<CToggleGroupItemProps> =>
      React.isValidElement(child) && child.type === CToggleGroupItem
  );

  const isSelected = useCallback((itemValue: string): boolean => {
    return props.value.includes(itemValue);
  }, [props.value]);

  const handleClick = useCallback((itemValue: string) => {
    if (exclusive) {
      props.onChange(props.value.includes(itemValue) ? [] : [itemValue]);
    } else {
      const next = props.value.includes(itemValue)
        ? props.value.filter(v => v !== itemValue)
        : [...props.value, itemValue];
      props.onChange(next);
    }
  }, [props.value, exclusive, props.onChange]);

  return (
    <CFlex
      id={props.id}
      testSelector={props.testSelector}
      style={props.style}
      direction={orientation === CToggleGroupOrientation.Vertical ? FlexDirection.Column : FlexDirection.Row}
      alignItems={AlignItems.Center}
      class={classNames('c-toggle-group', `c-toggle-group--${orientation}`, props.class)}
    >
      {items.map((child, index) => {
        const item = child.props;
        const selected = isSelected(item.value);
        return (
          <CButton
            key={item.value}
            icon={item.icon}
            text={item.text}
            size={size}
            shape={CButtonShape.Square}
            pattern={selected ? CButtonPattern.Primary : CButtonPattern.Secondary}
            disabled={item.disabled}
            onClick={() => handleClick(item.value)}
            class={classNames('c-toggle-group__item', {
              'c-toggle-group__item--first': index === 0,
              'c-toggle-group__item--last': index === items.length - 1,
              'c-toggle-group__item--middle': index > 0 && index < items.length - 1,
            })}
          />
        );
      })}
    </CFlex>
  );
}
